import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useBundle, useBundles } from '../hooks/useBundles';
import { useCurrency } from '../hooks/useCurrency';
import { BundleCard, imgSrc, discountLabel } from '../components/BundleCard';
import { api } from '../lib/api';
import type { Bundle, BundleProduct } from '../lib/catalogTypes';
import type { Cart } from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  fixed:       'Fixed Bundle',
  byob:        'Build Your Own',
  mix_match:   'Mix & Match',
  frame_print: 'Frame + Print',
};

const GOLD = '#C9A84C';
const DARK = '#1A1612';
const BG   = '#F9F7F4';

// ─── Bundle mode detection ────────────────────────────────────────────────────
// Fixed: every product is already required (required_quantity === products.length)
// BYOB:  user picks from a larger pool

function bundleMode(bundle: Bundle): 'fixed' | 'byob' {
  const required = bundle.min_products ?? bundle.required_quantity ?? bundle.products.length;
  return required >= bundle.products.length ? 'fixed' : 'byob';
}

// ─── Countdown ────────────────────────────────────────────────────────────────

function useCountdown(endsAt: string | null) {
  const [ms, setMs] = useState<number | null>(null);
  useEffect(() => {
    if (!endsAt) return;
    const target = new Date(endsAt).getTime();
    const tick = () => setMs(Math.max(0, target - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  if (ms === null) return null;
  return {
    d: Math.floor(ms / 86400000),
    h: Math.floor((ms % 86400000) / 3600000),
    m: Math.floor((ms % 3600000) / 60000),
    s: Math.floor((ms % 60000) / 1000),
    expired: ms === 0,
  };
}

function CountdownBlock({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="flex h-14 w-14 items-center justify-center rounded-xl text-2xl font-bold tabular-nums text-white shadow-sm"
        style={{ background: GOLD }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-[10px] font-medium uppercase tracking-wider text-stone">{label}</span>
    </div>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

function calcPricing(bundle: Bundle, products: BundleProduct[]) {
  const originalTotal = products.reduce((s, p) => s + Number(p.base_price) * p.quantity, 0);
  switch (bundle.discount_type) {
    case 'percentage': {
      const savings = originalTotal * (Number(bundle.discount_value) / 100);
      return { originalTotal, finalPrice: originalTotal - savings, savings };
    }
    case 'fixed_amount': {
      const savings = Math.min(Number(bundle.discount_value), originalTotal);
      return { originalTotal, finalPrice: originalTotal - savings, savings };
    }
    case 'fixed_price': {
      const fp = Number(bundle.discount_value);
      return { originalTotal, finalPrice: fp, savings: Math.max(0, originalTotal - fp) };
    }
    default:
      return { originalTotal, finalPrice: originalTotal, savings: 0 };
  }
}

// ─── Gallery ──────────────────────────────────────────────────────────────────

function Gallery({ bundle }: { bundle: Bundle }) {
  const images = useMemo(() => {
    const seen = new Set<string>();
    const list: string[] = [];
    [bundle.banner_image, bundle.image].forEach(p => {
      if (p && !seen.has(p)) { seen.add(p); list.push(p); }
    });
    return list;
  }, [bundle]);

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const mainSrc = imgSrc(images[active]);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape')     setLightbox(false);
      if (e.key === 'ArrowRight') setActive(i => (i + 1) % images.length);
      if (e.key === 'ArrowLeft')  setActive(i => (i - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox, images.length]);

  if (images.length === 0) return null;

  return (
    <>
      <div className="space-y-3" role="region" aria-label="Bundle images">
        <div
          className="relative overflow-hidden rounded-2xl bg-[#F2EDE5] cursor-zoom-in"
          style={{ aspectRatio: '16/10' }}
          onClick={() => mainSrc && setLightbox(true)}
        >
          {mainSrc ? (
            <img src={mainSrc} alt={bundle.title} className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.02]" loading="lazy" />
          ) : (
            <GalleryPlaceholder />
          )}
          {mainSrc && (
            <button
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-ink backdrop-blur-sm transition-colors hover:bg-white"
              onClick={e => { e.stopPropagation(); setLightbox(true); }}
              aria-label="View full image"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 16 16"><path d="M10 2h4v4M6 14H2v-4M14 10v4h-4M2 6V2h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            </button>
          )}
        </div>
        {images.length > 1 && (
          <div className="flex gap-2">
            {images.map((src, i) => {
              const t = imgSrc(src);
              return t ? (
                <button key={i} onClick={() => setActive(i)}
                  className="relative h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg transition-all"
                  style={{ outline: i === active ? `2px solid ${GOLD}` : '2px solid transparent', outlineOffset: 2 }}
                  aria-label={`Image ${i + 1}`} aria-pressed={i === active}
                >
                  <img src={t} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ) : null;
            })}
          </div>
        )}
      </div>

      {lightbox && mainSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" onClick={() => setLightbox(false)}>
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
          <img src={mainSrc} alt={bundle.title} className="relative z-10 max-h-[90vh] max-w-[90vw] rounded-xl object-contain shadow-2xl" onClick={e => e.stopPropagation()} />
          <button className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm hover:bg-white/20" onClick={() => setLightbox(false)} aria-label="Close">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 20 20"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
          {images.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                onClick={e => { e.stopPropagation(); setActive(i => (i - 1 + images.length) % images.length); }} aria-label="Previous">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 20 20"><path d="M13 4l-6 6 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
              <button className="absolute right-4 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                onClick={e => { e.stopPropagation(); setActive(i => (i + 1) % images.length); }} aria-label="Next">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 20 20"><path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}

function GalleryPlaceholder() {
  return (
    <div className="flex h-full items-center justify-center">
      <svg className="h-20 w-20 text-[#C8BFB2]" fill="none" viewBox="0 0 80 80" aria-hidden="true">
        <rect x="10" y="10" width="26" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <rect x="44" y="10" width="26" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="44" width="26" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" />
        <rect x="44" y="44" width="26" height="26" rx="3" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

// ─── Image placeholder ────────────────────────────────────────────────────────

function ProductImagePlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#F2EDE5] to-[#E8E0D4]">
      <svg className="h-10 w-10 text-[#C8BFB2]" fill="none" viewBox="0 0 40 40" aria-hidden="true">
        <rect x="4"  y="4"  width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
        <rect x="22" y="4"  width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
        <rect x="4"  y="22" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
        <rect x="22" y="22" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      </svg>
    </div>
  );
}

// ─── Option selection type ────────────────────────────────────────────────────

type OptionSelection = { size_id: number | null; frame_id: number | null; material_id: number | null };

// ─── Option pill picker ───────────────────────────────────────────────────────

function OptionPicker({
  label, options, value, onChange,
}: {
  label: string;
  options: { id: number; name: string }[];
  value: number | null;
  onChange: (id: number | null) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div>
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#9A8E80' }}>{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map(opt => (
          <button
            key={opt.id}
            type="button"
            onClick={e => { e.stopPropagation(); onChange(value === opt.id ? null : opt.id); }}
            className="rounded border px-2.5 py-1 text-xs font-medium transition-colors"
            style={{
              background: value === opt.id ? DARK : 'white',
              color: value === opt.id ? 'white' : '#6B6460',
              borderColor: value === opt.id ? DARK : '#DDD8D0',
            }}
          >
            {opt.name}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Fixed product card (included — no selection) ─────────────────────────────

function FixedProductRow({ product, availableSizes, availableFrames, availableMaterials, selection, onSelectOption, index }: {
  product: BundleProduct;
  availableSizes: { id: number; name: string }[];
  availableFrames: { id: number; name: string }[];
  availableMaterials: { id: number; name: string }[];
  selection: OptionSelection;
  onSelectOption: (key: keyof OptionSelection, id: number | null) => void;
  index: number;
}) {
  const src = imgSrc(product.image);
  const hasOptions = availableSizes.length > 0 || availableFrames.length > 0 || availableMaterials.length > 0;
  const needsChoice = (availableSizes.length > 0 && !selection.size_id)
    || (availableFrames.length > 0 && !selection.frame_id)
    || (availableMaterials.length > 0 && !selection.material_id);

  return (
    <div
      className="flex gap-4 rounded-2xl bg-white p-4 transition-all duration-200 hover:shadow-md"
      style={{
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        outline: needsChoice ? '2px solid #FCD34D' : '2px solid transparent',
        outlineOffset: -2,
      }}
    >
      {/* Step number */}
      <div
        className="hidden sm:flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white mt-0.5"
        style={{ background: GOLD }}
        aria-hidden="true"
      >
        {index + 1}
      </div>

      {/* Thumbnail */}
      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#F2EDE5]">
        {src ? (
          <img src={src} alt={product.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <ProductImagePlaceholder />
        )}
        {product.quantity > 1 && (
          <span
            className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: GOLD }}
          >
            ×{product.quantity}
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 min-w-0 flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{product.name}</p>
            <p className="mt-0.5 text-xs text-stone">EGP {Number(product.base_price).toFixed(0)}</p>
          </div>
          <span
            className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{ background: '#EDFAF3', color: '#1A7A4A', border: '1px solid #B3E8CC' }}
          >
            Included
          </span>
        </div>

        {/* Option pickers */}
        {hasOptions && (
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2.5">
            {availableSizes.length > 0 && (
              <OptionPicker label="Size" options={availableSizes} value={selection.size_id} onChange={id => onSelectOption('size_id', id)} />
            )}
            {availableFrames.length > 0 && (
              <OptionPicker label="Frame" options={availableFrames} value={selection.frame_id} onChange={id => onSelectOption('frame_id', id)} />
            )}
            {availableMaterials.length > 0 && (
              <OptionPicker label="Material" options={availableMaterials} value={selection.material_id} onChange={id => onSelectOption('material_id', id)} />
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-2 flex items-center justify-between">
          <Link
            to={`/products/${product.slug}`}
            className="text-xs font-medium text-stone transition-colors hover:text-ink focus-visible:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
          >
            View product →
          </Link>
          {hasOptions && needsChoice && (
            <span className="text-[10px] font-medium" style={{ color: '#C9A84C' }}>
              Choose options
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Selectable product card (BYOB) ───────────────────────────────────────────

function SelectableProductCard({
  product, selected, disabled, onToggle,
  availableSizes, availableFrames, availableMaterials, selection, onSelectOption,
}: {
  product: BundleProduct;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
  availableSizes: { id: number; name: string }[];
  availableFrames: { id: number; name: string }[];
  availableMaterials: { id: number; name: string }[];
  selection: OptionSelection;
  onSelectOption: (key: keyof OptionSelection, id: number | null) => void;
}) {
  const src = imgSrc(product.image);

  return (
    <div
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-white cursor-pointer outline-none"
      style={{
        boxShadow: selected
          ? `0 0 0 2.5px ${GOLD}, 0 8px 32px rgba(201,168,76,0.18)`
          : disabled
          ? '0 1px 8px rgba(0,0,0,0.04)'
          : '0 1px 8px rgba(0,0,0,0.06)',
        transform: selected ? 'translateY(-2px)' : '',
        opacity: disabled && !selected ? 0.55 : 1,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease, opacity 0.2s',
      }}
      onMouseEnter={e => {
        if (!disabled || selected)
          (e.currentTarget as HTMLElement).style.boxShadow = selected
            ? `0 0 0 2.5px ${GOLD}, 0 12px 40px rgba(201,168,76,0.22)`
            : '0 8px 32px rgba(0,0,0,0.10)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = selected
          ? `0 0 0 2.5px ${GOLD}, 0 8px 32px rgba(201,168,76,0.18)`
          : disabled ? '0 1px 8px rgba(0,0,0,0.04)' : '0 1px 8px rgba(0,0,0,0.06)';
      }}
      onClick={onToggle}
      onKeyDown={e => e.key === ' ' && onToggle()}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-[#F2EDE5]">
        {src ? (
          <img src={src} alt={product.name} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        ) : (
          <ProductImagePlaceholder />
        )}

        {/* Selected overlay */}
        {selected && (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: 'rgba(201,168,76,0.15)' }}
          >
            <div
              className="flex h-11 w-11 items-center justify-center rounded-full shadow-lg"
              style={{ background: GOLD }}
            >
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12l6 6 10-10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        )}

        {product.quantity > 1 && (
          <span
            className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold text-white"
            style={{ background: selected ? GOLD : '#1A1612' }}
          >
            ×{product.quantity}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <p className="line-clamp-2 text-sm font-medium leading-snug text-ink">{product.name}</p>
        <p className="mt-1 text-xs text-stone">EGP {Number(product.base_price).toFixed(0)}</p>
        <div className="mt-3 flex items-center justify-between">
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold"
            style={{ color: selected ? GOLD : '#9A8E80' }}
          >
            {selected ? (
              <>
                <svg className="h-3 w-3" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                Selected
              </>
            ) : 'Tap to select'}
          </span>
        </div>
        {selected && (availableSizes.length > 0 || availableFrames.length > 0 || availableMaterials.length > 0) && (
          <div className="mt-3 space-y-2.5 border-t border-[#F0EDE8] pt-3" onClick={e => e.stopPropagation()}>
            <OptionPicker label="Size" options={availableSizes} value={selection.size_id} onChange={id => onSelectOption('size_id', id)} />
            <OptionPicker label="Frame" options={availableFrames} value={selection.frame_id} onChange={id => onSelectOption('frame_id', id)} />
            <OptionPicker label="Material" options={availableMaterials} value={selection.material_id} onChange={id => onSelectOption('material_id', id)} />
          </div>
        )}
      </div>

      {/* Gold border glow when selected */}
      {selected && (
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl"
          style={{ border: `2px solid ${GOLD}` }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

// ─── Trust signals ────────────────────────────────────────────────────────────

function TrustSignals() {
  const items = [
    { icon: '🔒', label: 'Secure Checkout' },
    { icon: '🚚', label: 'Fast Shipping' },
    { icon: '⭐', label: 'Premium Quality' },
    { icon: '↩',  label: 'Easy Returns' },
  ];
  return (
    <div className="mt-5 grid grid-cols-2 gap-2 border-t border-[#EDE9E2] pt-5">
      {items.map(item => (
        <div key={item.label} className="flex items-center gap-2">
          <span className="text-base" aria-hidden="true">{item.icon}</span>
          <span className="text-xs text-stone">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Summary panel ────────────────────────────────────────────────────────────

function SummaryPanel({
  bundle, pricing, mode, selCount, required, canAdd,
  missingOptionProducts, onAdd, adding, added,
}: {
  bundle: Bundle;
  pricing: { originalTotal: number; finalPrice: number; savings: number };
  mode: 'fixed' | 'byob';
  selCount: number;
  required: number;
  canAdd: boolean;
  missingOptionProducts: BundleProduct[];
  onAdd: () => void;
  adding: boolean;
  added: boolean;
}) {
  const { format } = useCurrency();

  function ctaLabel(): string {
    if (added)  return '✓ Added to Cart';
    if (adding) return 'Adding…';
    if (missingOptionProducts.length > 0) return 'Choose options to continue';
    if (mode === 'byob') {
      if (selCount === 0)      return `Select ${required} Products`;
      if (selCount < required) return `Select ${required - selCount} More`;
    }
    return bundle.cta_text ?? 'Add Bundle to Cart';
  }

  return (
    <div
      className="rounded-2xl bg-white p-6"
      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
      aria-label="Bundle summary"
    >
      {/* Savings callout */}
      {pricing.savings > 0 && (
        <div className="mb-5 rounded-xl p-4 text-center" style={{ background: '#FBF6EC', border: '1px solid #E8D8A8' }}>
          <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#A07830' }}>Bundle savings</p>
          <p className="mt-1 text-2xl font-bold" style={{ color: GOLD }}>{discountLabel(bundle)}</p>
        </div>
      )}

      {/* Title */}
      <h2 className="font-serif text-lg font-light text-ink leading-snug">{bundle.title}</h2>

      {/* Pricing */}
      {pricing.originalTotal > 0 && (
        <div className="mt-4 space-y-1.5 border-b border-[#EDE9E2] pb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-stone">Original price</span>
            <span className={pricing.savings > 0 ? 'line-through text-stone' : 'font-medium text-ink'}>
              {format(pricing.originalTotal)}
            </span>
          </div>
          {pricing.savings > 0 && (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone">Savings</span>
                <span className="font-semibold" style={{ color: GOLD }}>− {format(pricing.savings)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-ink">Bundle price</span>
                <span className="text-xl font-bold text-ink">{format(pricing.finalPrice)}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* BYOB progress */}
      {mode === 'byob' && (
        <div className="my-4">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs text-stone">
              Selected <strong className="text-ink">{selCount}</strong> of <strong className="text-ink">{required}</strong>
            </span>
            {selCount >= required && (
              <span className="text-xs font-semibold" style={{ color: GOLD }}>Ready!</span>
            )}
          </div>
          <div className="overflow-hidden rounded-full bg-[#EDE9E2]" style={{ height: 6 }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(100, (selCount / required) * 100)}%`,
                background: selCount >= required ? GOLD : '#D4C9B8',
              }}
              role="progressbar"
              aria-valuenow={selCount}
              aria-valuemin={0}
              aria-valuemax={required}
            />
          </div>
        </div>
      )}

      {/* Fixed: product count */}
      {mode === 'fixed' && (
        <div className="my-4 flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: '#EDFAF3', color: '#1A7A4A', border: '1px solid #B3E8CC' }}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {bundle.products.length} {bundle.products.length === 1 ? 'product' : 'products'} included
          </span>
        </div>
      )}

      {/* Missing options warning */}
      {missingOptionProducts.length > 0 && (
        <div className="mt-4 rounded-xl px-4 py-3" style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#92400E' }}>
            Options needed
          </p>
          <ul className="space-y-1">
            {missingOptionProducts.map(p => (
              <li key={p.id} className="flex items-center gap-2 text-xs" style={{ color: '#B45309' }}>
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                <span className="truncate">{p.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onAdd}
        disabled={!canAdd || adding || added}
        className="mt-4 w-full rounded-xl py-4 text-sm font-semibold text-white transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          background: added ? '#2D7A4F' : missingOptionProducts.length > 0 ? '#C9A84C' : canAdd ? DARK : '#9A8E80',
          opacity: adding ? 0.8 : 1,
          cursor: !canAdd && !adding ? 'not-allowed' : 'pointer',
        }}
      >
        {ctaLabel()}
      </button>

      {bundle.ends_at && (
        <p className="mt-3 text-center text-xs text-stone">
          Offer ends {new Date(bundle.ends_at).toLocaleDateString('en-EG', { dateStyle: 'long' })}
        </p>
      )}

      <TrustSignals />
    </div>
  );
}

// ─── Mobile sticky bar ────────────────────────────────────────────────────────

function MobileStickyBar({
  bundle, pricing, mode, selCount, required, canAdd,
  missingOptionProducts, onAdd, adding, added,
}: {
  bundle: Bundle;
  pricing: { savings: number; finalPrice: number };
  mode: 'fixed' | 'byob';
  selCount: number;
  required: number;
  canAdd: boolean;
  missingOptionProducts: BundleProduct[];
  onAdd: () => void;
  adding: boolean;
  added: boolean;
}) {
  const { format } = useCurrency();

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 lg:hidden"
      style={{
        background: 'rgba(255,255,255,0.97)',
        backdropFilter: 'blur(12px)',
        borderTop: '1px solid #EDE9E2',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.08)',
      }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
        <div className="flex-1 min-w-0">
          {pricing.savings > 0 && (
            <p className="text-xs font-semibold" style={{ color: GOLD }}>Save {format(pricing.savings)}</p>
          )}
          <p className="text-sm font-bold text-ink truncate">{bundle.title}</p>
          {missingOptionProducts.length > 0 ? (
            <p className="text-xs font-medium" style={{ color: '#B45309' }}>
              Choose options for {missingOptionProducts.length} {missingOptionProducts.length === 1 ? 'item' : 'items'}
            </p>
          ) : mode === 'byob' && (
            <p className="text-xs text-stone">{selCount} / {required} selected</p>
          )}
        </div>
        <button
          onClick={onAdd}
          disabled={!canAdd || adding || added}
          className="shrink-0 rounded-xl px-5 py-3 text-sm font-semibold text-white transition-all"
          style={{
            background: added ? '#2D7A4F' : missingOptionProducts.length > 0 ? '#C9A84C' : canAdd ? DARK : '#9A8E80',
            cursor: !canAdd ? 'not-allowed' : 'pointer',
          }}
        >
          {added ? '✓ Added' : adding ? 'Adding…' : missingOptionProducts.length > 0 ? 'Choose options ↑' : bundle.cta_text ?? 'Add to Cart'}
        </button>
      </div>
    </div>
  );
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function DetailSkeleton() {
  const shimmer: React.CSSProperties = {
    background: 'linear-gradient(90deg,#EDE9E2 25%,#F5F2EE 50%,#EDE9E2 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
  };
  const bar = (w: string, h = '1rem') => (
    <div className="rounded-full" style={{ width: w, height: h, ...shimmer }} />
  );
  return (
    <div className="min-h-screen" style={{ background: BG }}>
      <div className="h-72 w-full md:h-96" style={shimmer} />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl" style={{ aspectRatio: '16/10', ...shimmer }} />
            <div className="space-y-3">{bar('40%', '1.5rem')}{bar('70%')}{bar('55%')}</div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-2xl" style={shimmer} />
              ))}
            </div>
          </div>
          <div className="h-80 rounded-2xl" style={shimmer} />
        </div>
      </div>
      <style>{`@keyframes shimmer { from { background-position:-200% 0 } to { background-position:200% 0 } }`}</style>
    </div>
  );
}

// ─── Fade-in wrapper ──────────────────────────────────────────────────────────

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'none' : 'translateY(20px)',
      transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function BundleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: bundle, isLoading, isError } = useBundle(slug ?? '');
  const { data: allBundles }                 = useBundles();
  const queryClient  = useQueryClient();
  const countdown    = useCountdown(bundle?.ends_at ?? null);

  const [adding, setAdding] = useState(false);
  const [added, setAdded]   = useState(false);
  const [selectedIds, setSelected] = useState<Set<number>>(new Set());
  const [productSelections, setProductSelections] = useState<Map<number, OptionSelection>>(new Map());

  if (isLoading) return <DetailSkeleton />;

  if (isError || !bundle) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <p className="font-serif text-2xl font-light text-stone">Bundle not found.</p>
        <Link to="/bundles" className="mt-6 inline-block rounded-full px-6 py-3 text-sm font-medium text-white" style={{ background: DARK }}>
          ← All Bundles
        </Link>
      </main>
    );
  }

  // ── Mode detection ────────────────────────────────────────────────────────
  const mode     = bundleMode(bundle);
  const required = bundle.min_products ?? bundle.required_quantity ?? bundle.products.length;
  const maxSel   = bundle.max_products ?? bundle.products.length;
  const selCount = selectedIds.size;

  const selectedProducts = bundle.products.filter(p => selectedIds.has(p.id));
  const pricingProducts  = mode === 'fixed' ? bundle.products : (selCount > 0 ? selectedProducts : bundle.products);
  const pricing          = calcPricing(bundle, pricingProducts);

  // ── Option selection helpers ──────────────────────────────────────────────
  function getSelection(pid: number): OptionSelection {
    return productSelections.get(pid) ?? { size_id: null, frame_id: null, material_id: null };
  }
  function setProductOption(pid: number, key: keyof OptionSelection, value: number | null) {
    setProductSelections(prev => {
      const next = new Map(prev);
      next.set(pid, { ...getSelection(pid), [key]: value });
      return next;
    });
  }
  function availableSizes(p: BundleProduct)     { return p.size_ids.length     > 0 ? p.sizes.filter(s     => p.size_ids.includes(s.id))     : p.sizes;     }
  function availableFrames(p: BundleProduct)    { return p.frame_ids.length    > 0 ? p.frames.filter(f    => p.frame_ids.includes(f.id))    : p.frames;    }
  function availableMaterials(p: BundleProduct) { return p.material_ids.length > 0 ? p.materials.filter(m => p.material_ids.includes(m.id)) : p.materials; }
  function productNeedsOption(p: BundleProduct): boolean {
    const sel = getSelection(p.id);
    if (availableSizes(p).length     > 0 && !sel.size_id)     return true;
    if (availableFrames(p).length    > 0 && !sel.frame_id)    return true;
    if (availableMaterials(p).length > 0 && !sel.material_id) return true;
    return false;
  }

  const toAddProducts = mode === 'fixed' ? bundle.products : selectedProducts;
  const missingOptionProducts = toAddProducts.filter(productNeedsOption);
  const hasUnpickedOptions = missingOptionProducts.length > 0;
  const canAdd = (mode === 'fixed' ? true : selCount >= required) && !hasUnpickedOptions;

  function toggleProduct(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < maxSel) {
        next.add(id);
      }
      return next;
    });
  }

  // ── Add to cart ───────────────────────────────────────────────────────────
  async function handleAdd() {
    if (!canAdd || !bundle) return;
    setAdding(true);
    try {
      const products = toAddProducts.map((p) => {
        const sel = getSelection(p.id);
        const entry: Record<string, unknown> = { product_id: p.id, quantity: p.quantity };
        if (sel.size_id)     entry.size_id     = sel.size_id;
        if (sel.frame_id)    entry.frame_id    = sel.frame_id;
        if (sel.material_id) entry.material_id = sel.material_id;
        return entry;
      });

      const res = await api.post<Cart>('/cart/bundles', {
        bundle_id: bundle.id,
        quantity: 1,
        products,
      });

      queryClient.setQueriesData<Cart>({ queryKey: ['cart'] }, res.data);
      setAdded(true);
      toast.success(`${bundle.title} added to cart`, {
        position: 'bottom-right',
        autoClose: 3000,
        hideProgressBar: true,
        closeButton: false,
        style: { borderRadius: '12px', fontFamily: 'inherit', fontSize: '14px', fontWeight: 500, padding: '12px 20px' },
      });
      setTimeout(() => setAdded(false), 3500);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Could not add to cart.';
      toast.error(msg, {
        position: 'bottom-right',
        autoClose: 4000,
        style: { borderRadius: '12px', fontFamily: 'inherit', fontSize: '13px' },
      });
    } finally {
      setAdding(false);
    }
  }

  const heroSrc = imgSrc(bundle.banner_image ?? bundle.image);
  const relatedBundles = (allBundles ?? [])
    .filter(b => b.slug !== bundle.slug)
    .sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0))
    .slice(0, 3);

  return (
    <div className="min-h-screen pb-24 lg:pb-0" style={{ background: BG }}>

      {/* ── Cinematic hero ───────────────────────────────────── */}
      <div className="relative overflow-hidden" style={{ background: DARK, minHeight: '38vh' }}>
        {heroSrc && (
          <img src={heroSrc} alt="" aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-45" loading="eager" />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.70) 100%)' }} />

        <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
          <nav className="mb-6 flex items-center gap-2 text-xs text-white/60" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-white/90">Home</Link>
            <span aria-hidden="true">/</span>
            <Link to="/bundles" className="transition-colors hover:text-white/90">Bundles</Link>
            <span aria-hidden="true">/</span>
            <span className="text-white/80">{bundle.title}</span>
          </nav>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            {bundle.is_featured && (
              <span className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white" style={{ background: GOLD }}>★ Featured</span>
            )}
            {bundle.type && (
              <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                {TYPE_LABELS[bundle.type] ?? bundle.type}
              </span>
            )}
            {mode === 'fixed' && (
              <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                {bundle.products.length} {bundle.products.length === 1 ? 'piece' : 'pieces'} included
              </span>
            )}
            {mode === 'byob' && (
              <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                Choose any {required}
              </span>
            )}
          </div>

          <h1 className="max-w-2xl font-serif text-3xl font-light leading-tight text-white md:text-4xl lg:text-5xl">
            {bundle.title}
          </h1>
          {bundle.description && (
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/75">{bundle.description}</p>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-4">
            {discountLabel(bundle) && (
              <span className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-white" style={{ background: 'rgba(201,168,76,0.85)', backdropFilter: 'blur(6px)' }}>
                {discountLabel(bundle)}
              </span>
            )}
            {bundle.ends_at && (
              <span className="text-sm text-white/70">
                Ends {new Date(bundle.ends_at).toLocaleDateString('en-EG', { dateStyle: 'long' })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ─────────────────────────────────────── */}
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3">

          {/* Left column */}
          <div className="space-y-10 lg:col-span-2">

            {/* Countdown */}
            {countdown && !countdown.expired && (
              <FadeIn>
                <div className="flex flex-col gap-4 rounded-2xl p-6 sm:flex-row sm:items-center sm:justify-between"
                  style={{ background: '#FBF6EC', border: '1px solid #E8D8A8' }}
                  aria-label="Countdown timer"
                >
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#A07830' }}>Offer ends in</p>
                    <p className="mt-1 text-sm" style={{ color: '#7A5C18' }}>Don't miss this limited-time deal.</p>
                  </div>
                  <div className="flex items-end gap-3" role="timer" aria-live="polite">
                    <CountdownBlock value={countdown.d} label="Days" />
                    <span className="mb-5 text-xl font-bold" style={{ color: GOLD }} aria-hidden="true">:</span>
                    <CountdownBlock value={countdown.h} label="Hours" />
                    <span className="mb-5 text-xl font-bold" style={{ color: GOLD }} aria-hidden="true">:</span>
                    <CountdownBlock value={countdown.m} label="Min" />
                    <span className="mb-5 text-xl font-bold" style={{ color: GOLD }} aria-hidden="true">:</span>
                    <CountdownBlock value={countdown.s} label="Sec" />
                  </div>
                </div>
              </FadeIn>
            )}

            {/* Gallery */}
            <FadeIn delay={60}>
              <Gallery bundle={bundle} />
            </FadeIn>

            {/* ── FIXED MODE: show all products, no checkboxes ── */}
            {mode === 'fixed' && bundle.products.length > 0 && (
              <FadeIn delay={120}>
                <section aria-label="Included products">
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl font-light text-ink">What's Included</h2>
                    <p className="mt-1 text-sm text-stone">
                      {bundle.products.length} {bundle.products.length === 1 ? 'piece' : 'pieces'} — everything you need, curated together.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {bundle.products.map((p, i) => (
                      <FixedProductRow
                        key={p.id}
                        product={p}
                        index={i}
                        availableSizes={availableSizes(p)}
                        availableFrames={availableFrames(p)}
                        availableMaterials={availableMaterials(p)}
                        selection={getSelection(p.id)}
                        onSelectOption={(key, id) => setProductOption(p.id, key, id)}
                      />
                    ))}
                  </div>
                </section>
              </FadeIn>
            )}

            {/* ── BYOB MODE: selectable product grid ─────────── */}
            {mode === 'byob' && bundle.products.length > 0 && (
              <FadeIn delay={120}>
                <section aria-label="Choose products">
                  {/* Header + progress */}
                  <div className="mb-6">
                    <h2 className="font-serif text-2xl font-light text-ink">Choose Your Products</h2>
                    <p className="mt-1 text-sm text-stone">
                      Select exactly <strong className="text-ink">{required}</strong> {required === 1 ? 'product' : 'products'} from the collection below.
                    </p>

                    <div className="mt-5 flex items-center gap-4 rounded-xl p-4" style={{ background: '#F5F2EE' }}>
                      <div className="flex-1 overflow-hidden rounded-full bg-[#DDD8D2]" style={{ height: 8 }}>
                        <div
                          className="h-full rounded-full transition-all duration-400"
                          style={{
                            width: `${Math.min(100, (selCount / required) * 100)}%`,
                            background: selCount >= required ? GOLD : '#C4BAA8',
                          }}
                          role="progressbar"
                          aria-valuenow={selCount}
                          aria-valuemin={0}
                          aria-valuemax={required}
                        />
                      </div>
                      <span className="shrink-0 text-sm font-semibold" style={{ color: selCount >= required ? GOLD : '#7A6E64' }}>
                        {selCount === 0
                          ? `Select ${required}`
                          : selCount >= required
                          ? '✓ Ready!'
                          : `${selCount} / ${required}`}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" role="group" aria-label="Select products">
                    {bundle.products.map(p => (
                      <SelectableProductCard
                        key={p.id}
                        product={p}
                        selected={selectedIds.has(p.id)}
                        disabled={selCount >= required && !selectedIds.has(p.id)}
                        onToggle={() => toggleProduct(p.id)}
                        availableSizes={availableSizes(p)}
                        availableFrames={availableFrames(p)}
                        availableMaterials={availableMaterials(p)}
                        selection={getSelection(p.id)}
                        onSelectOption={(key, id) => setProductOption(p.id, key, id)}
                      />
                    ))}
                  </div>
                </section>
              </FadeIn>
            )}

            {/* Category chips for BYOB */}
            {mode === 'byob' && bundle.categories.length > 0 && (
              <FadeIn delay={180}>
                <section aria-label="Browse categories">
                  <h2 className="mb-4 font-serif text-xl font-light text-ink">Browse by Category</h2>
                  <div className="flex flex-wrap gap-3">
                    {bundle.categories.map(cat => (
                      <Link key={cat.id} to={`/categories/${cat.slug}`}
                        className="rounded-full border border-[#DDD8D2] bg-white px-4 py-2 text-sm text-ink transition-all hover:border-[#C9A84C] hover:text-[#A07830] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]">
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </section>
              </FadeIn>
            )}

          </div>

          {/* Sticky right panel */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <SummaryPanel
                bundle={bundle}
                pricing={pricing}
                mode={mode}
                selCount={selCount}
                required={required}
                canAdd={canAdd}
                missingOptionProducts={missingOptionProducts}
                onAdd={handleAdd}
                adding={adding}
                added={added}
              />
              <Link
                to="/bundles"
                className="mt-4 flex items-center justify-center gap-1.5 text-xs text-stone transition-colors hover:text-ink focus-visible:outline-none"
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 14 14" aria-hidden="true">
                  <path d="M12 7H2M6 3L2 7l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                All Bundles
              </Link>
            </div>
          </div>
        </div>

        {/* Related bundles */}
        {relatedBundles.length > 0 && (
          <FadeIn delay={200}>
            <section className="mt-20" aria-label="Related bundles">
              <div className="mb-8 flex items-center gap-4">
                <span className="h-px flex-1 max-w-[60px]" style={{ background: GOLD }} aria-hidden="true" />
                <h2 className="font-serif text-2xl font-light text-ink">You May Also Like</h2>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {relatedBundles.map(b => <BundleCard key={b.id} bundle={b} />)}
              </div>
            </section>
          </FadeIn>
        )}
      </div>

      {/* Mobile sticky bar */}
      <MobileStickyBar
        bundle={bundle}
        pricing={pricing}
        mode={mode}
        selCount={selCount}
        required={required}
        canAdd={canAdd}
        missingOptionProducts={missingOptionProducts}
        onAdd={handleAdd}
        adding={adding}
        added={added}
      />
    </div>
  );
}
